import { useState, useEffect } from "react";
import { ModalForm } from "@/components/ModalForm";
import { CandidateFormFields } from "./CandidateFormFields";
import { CandidateFormProgress } from "./CandidateFormProgress";
import { Candidate } from "@/types/candidate";
import { createCandidate, CandidateFormData } from "@/utils/candidate";
import { extractInfoFromCV } from "@/utils/candidate/cv-extractor";
import { CVUploadZone } from "./CVUploadZone";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { LINKEDIN_NOT_CONNECTED, readLinkedInFetchError } from "@/utils/candidate/linkedinUtils";
import { supabase } from "@/integrations/supabase/client";
import { ProcessingOverlay } from "@/components/ui/ProcessingOverlay";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fetchCustomFields, CustomField, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useAuth } from "@/providers/AuthProvider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { User, FileText } from "lucide-react";
import { LanguageManager } from "@/components/ui/LanguageManager";
import { CandidateLanguage } from "@/types/candidate";

interface AddCandidateFormProps {
	isOpen: boolean;
	onClose: () => void;
	onCandidateAdded: (candidate: Candidate) => void;
	initialFormData?: CandidateFormData | null;
}

export const AddCandidateForm = ({ 
	isOpen, 
	onClose,
	onCandidateAdded,
	initialFormData = null
}: AddCandidateFormProps) => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const [formData, setFormData] = useState<CandidateFormData>({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		role: "",
		skills: "",
		summary: "",
		cvFile: null,
		linkedin: ""
	});
	
	const [loading, setLoading] = useState(false);
	const [processingStage, setProcessingStage] = useState<string | null>(null);
	const [progress, setProgress] = useState(0);
	const [fetchingLinkedInData, setFetchingLinkedInData] = useState(false);
	const { currentOrganization } = useAuth();
	const organizationId = currentOrganization?.id;
	const [fields, setFields] = useState<CustomField[]>([]);
	const [fieldsLoading, setFieldsLoading] = useState(false);
	const [fieldsError, setFieldsError] = useState<string | null>(null);
	const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
	const [languages, setLanguages] = useState<CandidateLanguage[]>([]);
	const [activeTab, setActiveTab] = useState("general");
	
	// Apply initial form data when provided
	useEffect(() => {
		if (initialFormData) {
			setFormData(initialFormData);
			if (initialFormData._initialLanguages && initialFormData._initialLanguages.length > 0) {
				setLanguages(initialFormData._initialLanguages.map(lang => ({
					id: '',
					candidate_id: '',
					language: lang.language,
					language_code: lang.language_code,
					cefr_level: lang.cefr_level as any,
					is_user_owned: false,
					created_at: new Date().toISOString()
				})));
			}
		}
	}, [initialFormData]);
	
	// Check for LinkedIn URL in the URL query parameters
	useEffect(() => {
		if (isOpen) {
			const params = new URLSearchParams(location.search);
			const linkedinUrl = params.get("linkedinUrl");
			
			//console.log('[AddCandidateForm] LinkedIn URL from params:', linkedinUrl);
			
			if (linkedinUrl && !initialFormData) {
				// Set the LinkedIn URL in the form
				setFormData(prev => ({
					...prev,
					linkedin: linkedinUrl
				}));
				
				// Fetch LinkedIn data from our edge function
				fetchLinkedInData(linkedinUrl);
			}
		}
	}, [isOpen, location.search, initialFormData]);

	// Function to fetch LinkedIn data using our edge function
	const fetchLinkedInData = async (profileUrl: string) => {
		try {
			setFetchingLinkedInData(true);
			setProcessingStage("Fetching LinkedIn profile data...");
			setProgress(20);
			
			//console.log("[AddCandidateForm] Fetching LinkedIn data for:", profileUrl);
			
			// Call our Edge Function to get the LinkedIn data
			const { data, error } = await supabase.functions.invoke('fetch-linkedin-profile', {
				body: { profileUrl },
				headers: {
					'Content-Type': 'application/json'
				}
			});
			
			//console.log("[AddCandidateForm] Edge function response:", data);
			
			if (error) {
				console.error("[AddCandidateForm] Error fetching LinkedIn profile:", error);
				const details = await readLinkedInFetchError(error);
				if (details.code === LINKEDIN_NOT_CONNECTED)
					toast({
						title: t("candidates.newCandidateModal.linkedinNotConnected"),
						description: t("candidates.newCandidateModal.linkedinNotConnectedDescription"),
						variant: "destructive",
						action: (
							<ToastAction altText={t("candidates.newCandidateModal.openIntegrations")} onClick={() => navigate("/settings?tab=integrations")}>
								{t("candidates.newCandidateModal.openIntegrations")}
							</ToastAction>
						)
					});
				else
					toast({
						title: t("candidates.newCandidateModal.linkedinDataError"),
						description: details.message || error.message || "Failed to fetch LinkedIn data",
						variant: "destructive"
					});
				setFetchingLinkedInData(false);
				setProcessingStage(null);
				setProgress(0);
				return;
			}
			
			setProgress(70);
			
			if (data && data.success && data.data) {
				//console.log("[AddCandidateForm] LinkedIn data received:", data.data);
				const profileData = data.data;
				
				// Update form data with LinkedIn information
				const updatedData = {
					...formData,
					firstName: profileData.firstName || formData.firstName,
					lastName: profileData.lastName || formData.lastName,
					email: profileData.email || formData.email,
					phone: profileData.phone || formData.phone,
					role: profileData.role || formData.role,
					skills: profileData.skills || formData.skills,
					summary: profileData.summary || formData.summary,
					linkedin: profileData.linkedin || profileUrl,
					linkedinJson: profileData.linkedinJson
				};
				
				//console.log("[AddCandidateForm] Setting form data with LinkedIn info:", updatedData);
				setFormData(updatedData);
				
				toast({
					title: t("candidates.newCandidateModal.linkedinDataReceived"),
					description: t("candidates.newCandidateModal.linkedinDataSuccess")
				});
			} else {
				console.error("[AddCandidateForm] Invalid or missing data structure from LinkedIn API:", data);
				toast({
					title: t("candidates.newCandidateModal.linkedinDataError"),
					description: "LinkedIn data structure was not as expected",
					variant: "destructive"
				});
			}
		} catch (error) {
			console.error("[AddCandidateForm] Error fetching LinkedIn data:", error);
			toast({
				title: t("candidates.newCandidateModal.linkedinDataError"),
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive"
			});
		} finally {
			setFetchingLinkedInData(false);
			setProcessingStage(null);
			setProgress(0);
		}
	};

	// Reset form when closed
	useEffect(() => {
		if (!isOpen && !initialFormData) {
			setFormData({
				firstName: "",
				lastName: "",
				email: "",
				phone: "",
				role: "",
				skills: "",
				summary: "",
				cvFile: null,
				linkedin: ""
			});
			setLanguages([]);
			setCustomFieldValues({});
		}
	}, [isOpen, initialFormData]);
	
	const handleChange = (field: keyof CandidateFormData, value: string) => {
		//console.log("[AddCandidateForm] Field changed:", field, value);
		setFormData(prev => ({ ...prev, [field]: value }));
	};
	
	const handleFileChange = async (file: File | null) => {
		setFormData(prev => ({ ...prev, cvFile: file }));
		
		// If a CV is uploaded, try to extract information from it
		if (file) {
			try {
				setProcessingStage("Extracting information from CV");
				setProgress(20);
				
				const extractedInfo = await extractInfoFromCV(file, (progress, status) => {
					setProgress(progress);
					setProcessingStage(status);
				});
				
				//console.log("[AddCandidateForm] Extracted CV information:", extractedInfo);
				
				if (extractedInfo) {
					// Update form with extracted information, but don't overwrite existing values
					const updatedFormData = {
						...formData,
						firstName: formData.firstName || extractedInfo.firstName || "",
						lastName: formData.lastName || extractedInfo.lastName || "",
						email: formData.email || extractedInfo.email || "",
						phone: formData.phone || extractedInfo.phone || "",
						role: formData.role || extractedInfo.role || "",
						skills: formData.skills || (extractedInfo.skills ? extractedInfo.skills.join(", ") : ""),
						summary: formData.summary || extractedInfo.summary || "",
						linkedin: formData.linkedin || extractedInfo.linkedin || "",
					};

					//console.log("[AddCandidateForm] Updating form data with extracted info:", updatedFormData);
					setFormData(updatedFormData);

					// Populate languages from extracted info if none have been added yet
					if (languages.length === 0 && extractedInfo.languages && extractedInfo.languages.length > 0) {
						const extractedLanguages: CandidateLanguage[] = extractedInfo.languages.map(lang => ({
							id: '',
							candidate_id: '',
							language: lang.language,
							language_code: lang.language_code,
							cefr_level: lang.cefr_level as any,
							is_user_owned: false,
							created_at: new Date().toISOString()
						}));
						setLanguages(extractedLanguages);
					}
				}
			} catch (error) {
				console.error("[AddCandidateForm] Error extracting info from CV:", error);
				toast({
					title: "CV Processing Error",
					description: "Failed to extract information from CV",
					variant: "destructive"
				});
			} finally {
				setProcessingStage(null);
				setProgress(0);
			}
		}
	};
	
	const handleFetchLinkedInProfile = () => {
		if (formData.linkedin) {
			fetchLinkedInData(formData.linkedin);
		} else {
			toast({
				title: "LinkedIn URL Required",
				description: "Please enter a LinkedIn profile URL first",
				variant: "destructive"
			});
		}
	};
	
	const updateProgress = (stage: string | null, progress: number) => {
		setProcessingStage(stage);
		setProgress(progress);
	};
	
	useEffect(() => {
		if (!organizationId) return;
		setFieldsLoading(true);
		setFieldsError(null);
		fetchCustomFields(organizationId)
			.then((data: CustomFieldsJSON) => {
				setFields(data.Candidates || []);
			})
			.catch(() => {
				setFieldsError("Failed to load custom fields");
			})
			.finally(() => setFieldsLoading(false));
	}, [organizationId]);

	const handleCustomFieldChange = (name: string, value: any) => {
		setCustomFieldValues((prev) => ({ ...prev, [name]: value }));
	};
	
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!organizationId) {
			toast({
				title: t("common.error"),
				description: "No organization selected",
				variant: "destructive"
			});
			return;
		}
		
		setLoading(true);
		
		try {
			// Sanitize form data to prevent circular references
			const sanitizedFormData = {
				...formData,
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				phone: formData.phone,
				role: formData.role,
				skills: formData.skills,
				linkedin: formData.linkedin,
				summary: formData.summary ? String(formData.summary) : "",
				cvFile: formData.cvFile,
				linkedinJson: formData.linkedinJson,
				custom_fields: fields.length > 0 ? customFieldValues : undefined,
			};
			
			// Combine first and last name for the database
			const candidateData = await createCandidate(
				{
					...sanitizedFormData,
					name: `${sanitizedFormData.firstName} ${sanitizedFormData.lastName}`.trim()
				}, 
				organizationId!,
				updateProgress
			);
			
			if (candidateData) {
				// Create languages if any were added
				if (languages.length > 0) {
					try {
						const { error } = await supabase
							.from('candidate_languages')
							.insert(
								languages.map(lang => ({
									candidate_id: candidateData.id,
									language: lang.language,
									language_code: lang.language_code,
									cefr_level: lang.cefr_level,
									is_user_owned: lang.is_user_owned
								}))
							);
						
						if (error) throw error;
					} catch (error) {
						console.error('Error creating candidate languages:', error);
						toast({
							title: "Warning",
							description: "Candidate created but failed to save languages",
							variant: "destructive",
						});
					}
				}
				
				onCandidateAdded(candidateData);
				
				setFormData({
					firstName: "",
					lastName: "",
					email: "",
					phone: "",
					role: "",
					skills: "",
					summary: "",
					linkedin: "",
					cvFile: null
				});
				setLanguages([]);
				setCustomFieldValues({});
				setProcessingStage(null);
				setProgress(0);
			}
		} finally {
			setLoading(false);
		}
	};
	
	return (
		<ModalForm
			title={t("candidates.newCandidateModal.title")}
			isOpen={isOpen}
			onClose={onClose}
			onSubmit={handleSubmit}
			submitText={loading || fetchingLinkedInData ? t("common.loading") : t("candidates.newCandidateModal.saveButton")}
			submitDisabled={loading || fetchingLinkedInData}
			cancelText={t("candidates.newCandidateModal.cancelButton")}
		>
			<ProcessingOverlay 
				isProcessing={loading || fetchingLinkedInData}
				progress={progress}
				status={processingStage}
				message={loading ? t("candidates.newCandidateModal.creatingCandidate") : t("candidates.newCandidateModal.fetchingLinkedIn")}
			/>
			<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
				<TabsList className="mb-4">
					<TabsTrigger value="general">
						<User className="h-4 w-4 mr-2" />
						{t("candidates.newCandidateModal.generalTab", "General")}
					</TabsTrigger>
					{fields.length > 0 && (
						<TabsTrigger value="custom-fields">
							<FileText className="h-4 w-4 mr-2" />
							{t("candidates.tabs.customFields", "Custom Fields")}
						</TabsTrigger>
					)}
				</TabsList>
				<TabsContent value="general">
					<div className="space-y-4">
						{/* Only show summary if it exists */}
						{formData.summary && formData.summary.trim() !== "" && (
							<div className="mb-2">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									{t("candidates.newCandidateModal.summaryLabel")}
								</label>
								<div className="bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 text-gray-800 dark:text-gray-200 min-h-[48px]">
									{formData.summary}
								</div>
							</div>
						)}
						<p className="text-gray-600 dark:text-gray-400 text-sm">{t("candidates.newCandidateModal.description")}</p>
						<CandidateFormFields 
							formData={formData}
							onChange={handleChange}
							onFileChange={handleFileChange}
							disabled={loading || fetchingLinkedInData}
							onFetchLinkedIn={handleFetchLinkedInProfile}
							hideSummaryField={true}
						>
							{/* Languages Section - Between LinkedIn and Skills */}
							<div className="space-y-2">
								<LanguageManager
									languages={languages.map(lang => ({
										id: lang.id,
										language: lang.language,
										language_code: lang.language_code,
										level: lang.cefr_level,
										is_user_owned: lang.is_user_owned
									}))}
									onChange={(updatedLanguages) => {
										const newLanguages: CandidateLanguage[] = updatedLanguages.map(lang => ({
											id: lang.id || '',
											candidate_id: '', // Will be set when candidate is created
											language: lang.language,
											language_code: lang.language_code,
											cefr_level: lang.level,
											is_user_owned: lang.is_user_owned,
											created_at: new Date().toISOString()
										}));
										setLanguages(newLanguages);
									}}
									type="candidate"
									disabled={loading || fetchingLinkedInData}
								/>
							</div>
						</CandidateFormFields>
					</div>
				</TabsContent>
				{fields.length > 0 && (
					<TabsContent value="custom-fields">
						<div className="space-y-4">
							{fieldsLoading && <div>{t("candidates.customFields.loading", "Loading custom fields...")}</div>}
							{fieldsError && <div className="text-red-600">{fieldsError}</div>}
							{!fieldsLoading && !fieldsError && fields.length === 0 && (
								<div className="text-gray-500">{t("candidates.customFields.noFields", "No custom fields configured.")}</div>
							)}
							{!fieldsLoading && !fieldsError && fields.length > 0 && (
								<form className="space-y-6 pt-2">
									{fields.map((field) => {
										const value = customFieldValues[field.name] ?? "";
										return (
											<div key={field.name} className="flex flex-col gap-1">
												<label className="font-medium text-sm mb-1">{field.name}</label>
												{field.type === "text" && (
													<Input
														value={value}
														onChange={e => handleCustomFieldChange(field.name, e.target.value)}
														placeholder={field.name}
														aria-label={field.name}
													/>
												)}
												{field.type === "number" && (
													<Input
														type="number"
														value={value}
														onChange={e => handleCustomFieldChange(field.name, e.target.value)}
														placeholder={field.name}
														aria-label={field.name}
													/>
												)}
												{field.type === "boolean" && (
													<div className="flex items-center gap-2">
														<Checkbox
															checked={!!value}
															onCheckedChange={checked => handleCustomFieldChange(field.name, checked)}
															id={field.name}
															aria-label={field.name}
														/>
														<label htmlFor={field.name} className="text-sm">{field.name}</label>
													</div>
												)}
												{field.type === "dropdown" && field.options && (
													<Select
														value={value}
														onValueChange={val => handleCustomFieldChange(field.name, val)}
													>
														<SelectTrigger>
															<SelectValue placeholder={t("candidates.customFields.selectOption", { field: field.name })} />
														</SelectTrigger>
														<SelectContent>
															{field.options.map(opt => (
																<SelectItem key={opt} value={opt}>{opt}</SelectItem>
															))}
														</SelectContent>
													</Select>
												)}
											</div>
										);
									})}
								</form>
							)}
						</div>
					</TabsContent>
				)}
			</Tabs>
		</ModalForm>
	);
};
